import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { fmtUSD, fmtNum, fmtDate, relDays, scoreTone, CHART_COLORS } from "../lib/format";
import {
  Overline, Monogram, ScoreBar, RiskBadge, VerificationBadge, ConfidenceBadge, Tag, KV, SectionCard, EmptyState, Disclaimer,
} from "../components/shared";
import { useCompare } from "../context/CompareContext";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Markdown } from "../components/Markdown";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import {
  RefreshCw, ExternalLink, GitCompareArrows, Loader2, ArrowLeft, ShieldAlert, Github, Linkedin, Twitter, CheckCircle2, Circle, Clock, Lightbulb, Sparkles, Wand2,
} from "lucide-react";

function ScoreRing({ score }) {
  const t = scoreTone(score);
  const r = 34, c = 2 * Math.PI * r, off = c - (score / 100) * c;
  return (
    <div className="relative w-24 h-24">
      <svg className="w-24 h-24 -rotate-90" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r={r} fill="none" stroke="hsl(214 24% 90%)" strokeWidth="7" />
        <circle cx="40" cy="40" r={r} fill="none" stroke={t.bar} strokeWidth="7" strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.7s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn("num font-heading font-extrabold text-2xl", t.text)}>{score}</span>
        <span className="text-[9px] text-muted-foreground overline">/100</span>
      </div>
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6">{children}</div>;
}

const STATUS_ICON = { Completed: CheckCircle2, "In progress": Clock, Planned: Circle };

function PlainEnglish({ projectId, initial }) {
  const [text, setText] = useState(initial || "");
  const [loading, setLoading] = useState(false);

  const generate = async (refresh) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/explain${refresh ? "?refresh=true" : ""}`);
      setText(data.plain_summary);
      if (!data.llm_ok) toast.warning("Showing a simple template explanation (AI writer unavailable).");
    } catch { toast.error("Could not generate the explanation"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!initial) generate(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4" data-testid="plain-english">
      <div className="bg-accent border border-border/60 p-4 flex items-start gap-3">
        <div className="w-9 h-9 bg-primary rounded-sm flex items-center justify-center shrink-0"><Lightbulb className="w-5 h-5 text-primary-foreground" /></div>
        <div>
          <div className="font-heading font-bold text-sm">No jargon — the project explained for beginners</div>
          <p className="text-xs text-muted-foreground mt-0.5">A plain-language summary of what this project actually does and why, written for someone brand new to crypto.</p>
        </div>
      </div>

      <SectionCard
        title="In Plain English"
        right={text && !loading && (
          <button onClick={() => generate(true)} data-testid="plain-regenerate" className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline">
            <Wand2 className="w-3.5 h-3.5" /> Explain it even more simply
          </button>
        )}
      >
        {loading && !text ? (
          <div className="flex flex-col items-center justify-center py-14 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground mb-3" />
            <div className="text-sm font-medium">Putting this into plain English…</div>
            <div className="text-xs text-muted-foreground mt-1">Writing a beginner-friendly explanation.</div>
          </div>
        ) : text ? (
          <div className="max-w-3xl">
            {loading && <div className="text-xs text-muted-foreground mb-2 inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Rewriting…</div>}
            <Markdown>{text}</Markdown>
          </div>
        ) : (
          <EmptyState title="No explanation yet" sub="Generate a simple, beginner-friendly explanation of this project." icon={Sparkles} />
        )}
        <Disclaimer className="mt-4">This explains the concept in simple terms only. It is research, not investment advice.</Disclaimer>
      </SectionCard>
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [researching, setResearching] = useState(false);
  const { ids, toggle } = useCompare();

  const load = () => api.get(`/projects/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
  useEffect(() => { setP(null); load(); }, [id]);

  const runResearch = async () => {
    setResearching(true);
    try {
      const { data } = await api.post(`/projects/${id}/research`);
      setP((prev) => ({ ...prev, ai_summary: data.ai_summary, last_checked: data.last_checked }));
      toast[data.llm_ok ? "success" : "warning"](data.llm_ok ? "Research updated" : "Research ran (AI summary unchanged — LLM unavailable)");
    } catch { toast.error("Research failed"); }
    finally { setResearching(false); }
  };

  if (p === null) return <div className="flex items-center justify-center h-96"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  if (p === false) return <EmptyState title="Project not found" />;

  const t = p.tokenomics || {}, l = p.launch || {}, tech = p.technology || {}, sc = p.scores || {};
  const selected = ids.includes(p.id);
  const risk = (p.red_flags || []).reduce((acc, f) => {
    const order = { Critical: 5, High: 4, Medium: 3, Low: 2, Informational: 1 };
    return order[f.severity] > order[acc] ? f.severity : acc;
  }, "None");

  const compScore = (name) => (sc.components || []).find((c) => c.name === name)?.score ?? "—";

  const TABS = [
    "Overview", "In Plain English", "Use Case", "Technology", "Tokenomics", "Sale / Launch", "Whitepaper", "Smart Contracts",
    "Team", "Investors", "Legal", "Community", "Marketing", "Exchange / Liquidity", "Roadmap", "Post-Launch",
    "Risk / Red Flags", "Sources", "AI Analysis",
  ];

  return (
    <div className="space-y-5 fade-up" data-testid="project-detail-page">
      <Link to="/explorer" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Explorer
      </Link>

      {/* Header */}
      <div className="bg-card border border-border/60 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
          <Monogram symbol={p.token_symbol} name={p.name} size={64} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading font-extrabold text-3xl tracking-tight">{p.name}</h1>
              <span className="num text-sm text-muted-foreground">${p.token_symbol}</span>
              {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary transition-colors"><ExternalLink className="w-4 h-4" /></a>}
            </div>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-3xl">{p.short_description}</p>
            <div className="flex items-center gap-2 flex-wrap mt-3">
              <Tag>{p.category}</Tag>
              {(p.chains || []).map((c) => <Tag key={c}>{c}</Tag>)}
              <Tag>{p.stage}</Tag>
              <RiskBadge level={risk} count={(p.red_flags || []).length || undefined} />
              <ConfidenceBadge level={p.confidence} />
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <ScoreRing score={sc.overall || 0} />
              <div className="overline text-muted-foreground mt-1">Intelligence Score</div>
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={runResearch} disabled={researching} data-testid="run-research-project" className="inline-flex items-center gap-2 h-9 px-3.5 bg-primary text-primary-foreground text-sm font-semibold rounded-sm hover:bg-primary/90 disabled:opacity-60 transition-colors whitespace-nowrap">
                <RefreshCw className={cn("w-4 h-4", researching && "animate-spin")} /> Run Research Now
              </button>
              <button onClick={() => toggle(p.id)} data-testid="detail-compare-btn" className={cn("inline-flex items-center gap-2 h-9 px-3.5 text-sm font-semibold rounded-sm border transition-colors whitespace-nowrap", selected ? "bg-accent border-primary" : "border-border hover:bg-muted")}>
                <GitCompareArrows className="w-4 h-4" /> {selected ? "In compare" : "Add to compare"}
              </button>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mt-6 pt-5 border-t border-border/60">
          {[["Stage", p.stage], ["Jurisdiction", p.jurisdiction], ["FDV", fmtUSD(t.fdv)], ["Raised", fmtUSD(l.funds_raised)], ["TGE", fmtDate(l.tge_date)], ["Last research", relDays(p.last_checked)]].map(([k, v]) => (
            <div key={k}><div className="overline text-muted-foreground">{k}</div><div className="text-sm font-semibold mt-0.5 num">{v}</div></div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="Overview" className="w-full">
        <div className="bg-card border border-border/60 sticky top-16 z-10">
          <TabsList className="w-full h-auto flex-wrap justify-start bg-transparent p-1 gap-0.5">
            {TABS.map((tb) => (
              <TabsTrigger key={tb} value={tb} data-testid={`tab-${tb.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-sm px-2.5 py-1.5">
                {tb}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="mt-4">
          <TabsContent value="Overview">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 space-y-4">
                <SectionCard title="Executive Summary">
                  <Markdown>{p.ai_summary || p.short_description}</Markdown>
                  <Disclaimer className="mt-3">AI-generated synthesis of structured facts. Distinguish confirmed facts from inference — not investment advice.</Disclaimer>
                </SectionCard>
                <SectionCard title="Problem & Solution">
                  <Grid>
                    <KV label="Problem">{p.problem}</KV>
                    <KV label="Proposed Solution">{p.solution}</KV>
                    <KV label="Target Users">{p.target_users}</KV>
                    <KV label="Target Geography">{p.target_geography}</KV>
                    <KV label="Business Model">{p.business_type}</KV>
                    <KV label="Web Dependency">{p.web_dependency}</KV>
                  </Grid>
                </SectionCard>
                <SectionCard title="Competitive Landscape">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <div className="overline text-muted-foreground mb-2">Competitors</div>
                      <div className="flex flex-wrap gap-1.5">{(p.competitors || []).length ? p.competitors.map((c) => <Tag key={c}>{c}</Tag>) : <span className="text-sm text-muted-foreground">Not Found</span>}</div>
                    </div>
                    <div>
                      <div className="overline text-muted-foreground mb-2">Key Differentiators</div>
                      <ul className="text-sm space-y-1">{(p.differentiators || []).map((d) => <li key={d} className="flex gap-2"><span className="text-primary">▸</span>{d}</li>)}</ul>
                    </div>
                  </div>
                </SectionCard>
              </div>
              <div className="space-y-4">
                <SectionCard title="Quick Facts">
                  <KV label="Founding Year">{p.founding_year}</KV>
                  <KV label="Country / Jurisdiction">{p.jurisdiction}</KV>
                  <KV label="Sectors">{(p.sectors || []).join(", ")}</KV>
                  <KV label="Blockchain">{tech.blockchain}</KV>
                  <KV label="Token Standard">{tech.token_standard}</KV>
                  <KV label="Decentralization">{tech.decentralization_level}</KV>
                  <KV label="Status">{p.status}</KV>
                </SectionCard>
                <SectionCard title="Data Freshness">
                  <KV label="First Discovered">{fmtDate(p.first_discovered)}</KV>
                  <KV label="Last Checked">{fmtDate(p.last_checked)} · {relDays(p.last_checked)}</KV>
                  <KV label="Last Changed">{fmtDate(p.last_changed)}</KV>
                  <KV label="Next Scheduled Check">{fmtDate(p.next_check)}</KV>
                </SectionCard>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="In Plain English">
            <PlainEnglish projectId={id} initial={p.plain_summary} />
          </TabsContent>

          <TabsContent value="Use Case">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {[["Use Case Strength", compScore("Use Case")], ["Token Necessity", compScore("Token Necessity")], ["Market Opportunity", compScore("Competitive Differentiation")], ["Differentiation", compScore("Competitive Differentiation")]].map(([k, v]) => (
                <div key={k} className="bg-card border border-border/60 p-4">
                  <div className="overline text-muted-foreground">{k}</div>
                  <div className="num font-heading font-extrabold text-3xl mt-1">{v}</div>
                  <div className="mt-2"><ScoreBar score={typeof v === "number" ? v : 0} showValue={false} /></div>
                </div>
              ))}
            </div>
            <SectionCard title="Use Case Analysis">
              <Grid>
                <KV label="Problem Statement">{p.problem}</KV>
                <KV label="Proposed Solution">{p.solution}</KV>
                <KV label="Need for Blockchain">{tech.decentralization_level ? "Assessed — see decentralization rationale" : "Not Found"}</KV>
                <KV label="Token Necessity">{(t.utilities || []).length ? `Yes — ${t.utilities.join(", ")}` : "Under review"}</KV>
                <KV label="Target Market">{p.target_geography}</KV>
                <KV label="Business / Monetization">{p.business_type}</KV>
              </Grid>
            </SectionCard>
          </TabsContent>

          <TabsContent value="Technology">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <SectionCard title="Architecture" className="lg:col-span-2">
                <Grid>
                  {[["Blockchain", tech.blockchain], ["Blockchain Type", tech.blockchain_type], ["Layer", tech.layer], ["Token Standard", tech.token_standard], ["Contract Language", tech.contract_language], ["Consensus", tech.consensus], ["Oracle", tech.oracle], ["Wallet", tech.wallet], ["Custody", tech.custody], ["ZK", tech.zk], ["MPC", tech.mpc], ["Upgradeability", tech.upgradeability]].map(([k, v]) => <KV key={k} label={k}>{v}</KV>)}
                </Grid>
              </SectionCard>
              <SectionCard title="Decentralization Classification">
                <div className={cn("inline-flex px-2.5 py-1 rounded-sm text-sm font-semibold border", scoreTone(compScore("Decentralization")).text, scoreTone(compScore("Decentralization")).bg, scoreTone(compScore("Decentralization")).border)}>
                  {tech.decentralization_level}
                </div>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{tech.decentralization_reason}</p>
              </SectionCard>
            </div>
          </TabsContent>

          <TabsContent value="Tokenomics">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <SectionCard title="Supply & Valuation">
                {[["Total Supply", fmtNum(t.total_supply)], ["Max Supply", fmtNum(t.max_supply)], ["Circulating @ TGE", `${fmtNum(t.circulating_at_tge)} (${t.circulating_pct}%)`], ["Initial Market Cap", fmtUSD(t.initial_mcap)], ["Fully Diluted Valuation", fmtUSD(t.fdv)], ["Public Price", t.public_price ? `$${t.public_price}` : "—"], ["Private Price", t.private_price ? `$${t.private_price}` : "—"], ["Seed Price", t.seed_price ? `$${t.seed_price}` : "—"], ["Listing Price", t.listing_price ? `$${t.listing_price}` : "—"]].map(([k, v]) => <KV key={k} label={k}>{v}</KV>)}
              </SectionCard>
              <SectionCard title="Token Allocation">
                {(t.allocations || []).length ? (
                  <>
                    <div className="h-52">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={t.allocations} dataKey="pct" nameKey="category" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                            {t.allocations.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                          </Pie>
                          <Tooltip formatter={(v, n) => [`${v}%`, n]} contentStyle={{ fontSize: 12, borderRadius: 4 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-1 mt-2">
                      {t.allocations.map((a, i) => (
                        <div key={a.category} className="flex items-center justify-between text-xs">
                          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />{a.category}</span>
                          <span className="num font-semibold">{a.pct}%</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : <EmptyState title="No allocation data" />}
              </SectionCard>
              <div className="space-y-4">
                <SectionCard title="Vesting Schedule">
                  {(t.vesting || []).length ? (
                    <table className="w-full text-xs">
                      <thead><tr className="text-muted-foreground overline"><th className="text-left pb-1">Group</th><th className="text-right pb-1">Cliff</th><th className="text-right pb-1">Vest</th><th className="text-right pb-1">TGE</th></tr></thead>
                      <tbody>{t.vesting.map((v) => <tr key={v.category} className="border-t border-border/40"><td className="py-1.5">{v.category}</td><td className="text-right num">{v.cliff_months}m</td><td className="text-right num">{v.duration_months}m</td><td className="text-right num">{v.initial_unlock_pct}%</td></tr>)}</tbody>
                    </table>
                  ) : <span className="text-sm text-muted-foreground">Not Found</span>}
                </SectionCard>
                <SectionCard title="Economics & Utility">
                  <div className="flex flex-wrap gap-1.5 mb-3">{(t.utilities || []).map((u) => <Tag key={u}>{u}</Tag>)}</div>
                  {t.economics && <>
                    <KV label="Model">{t.economics.inflationary ? "Inflationary" : "Non-inflationary"}{t.economics.burn ? " · Burn" : ""}{t.economics.deflationary ? " · Deflationary" : ""}</KV>
                    <KV label="Staking Yield">{t.economics.staking_yield}</KV>
                    <KV label="Notes">{t.economics.notes}</KV>
                  </>}
                </SectionCard>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="Sale / Launch">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SectionCard title="Launch Timeline">
                {[["Announcement", l.announcement_date], ["Presale", l.presale_date], ["Public Sale", l.public_sale_date], ["TGE", l.tge_date], ["Mainnet", l.mainnet_date]].map(([k, v]) => (
                  <div key={k} className="flex items-center gap-3 py-2 border-b border-border/40 last:border-0">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                    <span className="text-sm flex-1">{k}</span>
                    <span className="num text-xs text-muted-foreground">{fmtDate(v)} · {relDays(v)}</span>
                  </div>
                ))}
              </SectionCard>
              <SectionCard title="Fundraising">
                <Grid>
                  {[["Hard Cap", fmtUSD(l.hard_cap)], ["Soft Cap", fmtUSD(l.soft_cap)], ["Target", fmtUSD(l.fundraising_target)], ["Funds Raised", fmtUSD(l.funds_raised)], ["Launchpad", l.launchpad], ["Exchange Partner", l.exchange_partner], ["Min Contribution", l.min_contribution ? fmtUSD(l.min_contribution) : "—"], ["Max Contribution", l.max_contribution ? fmtUSD(l.max_contribution) : "—"], ["KYC Required", l.kyc_required ? "Yes" : "No"]].map(([k, v]) => <KV key={k} label={k}>{v}</KV>)}
                </Grid>
                <div className="mt-2"><div className="overline text-muted-foreground mb-1">Accepted Currencies</div><div className="flex flex-wrap gap-1.5">{(l.accepted_currencies || []).map((c) => <Tag key={c}>{c}</Tag>)}</div></div>
                <div className="mt-2"><div className="overline text-muted-foreground mb-1">Geo Restrictions</div><div className="flex flex-wrap gap-1.5">{(l.geo_restrictions || []).map((c) => <Tag key={c}>{c}</Tag>)}</div></div>
              </SectionCard>
              <SectionCard title="Sale Phases" className="lg:col-span-2">
                {(l.sale_phases || []).length ? (
                  <table className="w-full text-sm">
                    <thead><tr className="overline text-muted-foreground"><th className="text-left pb-2">Phase</th><th className="text-right pb-2">Price</th><th className="text-right pb-2">Date</th></tr></thead>
                    <tbody>{l.sale_phases.map((s) => <tr key={s.name} className="border-t border-border/40"><td className="py-2">{s.name}</td><td className="text-right num">${s.price}</td><td className="text-right num text-muted-foreground">{fmtDate(s.date)}</td></tr>)}</tbody>
                  </table>
                ) : <span className="text-sm text-muted-foreground">Not Found</span>}
              </SectionCard>
            </div>
          </TabsContent>

          <TabsContent value="Whitepaper">
            <SectionCard title="Whitepaper Intelligence" right={p.whitepaper?.url && <a href={p.whitepaper.url} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1 hover:underline">Open whitepaper <ExternalLink className="w-3 h-3" /></a>}>
              <div className="text-xs text-muted-foreground mb-3">Version: <span className="num">{p.whitepaper?.version || "—"}</span></div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(p.whitepaper?.scores || {}).map(([k, v]) => (
                  <div key={k} className="border border-border/60 p-3"><div className="overline text-muted-foreground">{k}</div><div className="flex items-center gap-2 mt-1.5"><span className="num font-heading font-bold text-lg">{v}</span><div className="flex-1"><ScoreBar score={v} showValue={false} /></div></div></div>
                ))}
              </div>
              <Disclaimer className="mt-4">Automated whitepaper analysis. Insights should be verified against the source document; this is not a formal audit.</Disclaimer>
            </SectionCard>
          </TabsContent>

          <TabsContent value="Smart Contracts">
            {(p.contracts || []).length ? p.contracts.map((c, i) => (
              <SectionCard key={i} title={`${c.chain} Contract`} className="mb-4" right={<span className={cn("text-xs px-2 py-0.5 rounded-sm border", c.verified ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-200")}>{c.verified ? "Verified" : "Unverified"}</span>}>
                <div className="num text-xs bg-muted/60 border border-border/60 rounded-sm px-2 py-1.5 mb-3 break-all">{c.address}</div>
                <Grid>
                  <KV label="Proxy">{c.proxy ? "Yes" : "No"}</KV>
                  <KV label="Upgradeable">{c.upgradeable ? "Yes" : "No"}</KV>
                  <KV label="Audit">{c.audit ? `${c.audit.company} (${fmtDate(c.audit.date)})` : "None"}</KV>
                </Grid>
                {c.audit && <div className="text-xs text-muted-foreground mt-1">Resolved: {c.audit.resolved} · Unresolved: <span className={c.audit.unresolved > 0 ? "text-red-600 font-semibold" : ""}>{c.audit.unresolved}</span> {c.audit.report && <a href={c.audit.report} target="_blank" rel="noreferrer" className="text-primary hover:underline ml-1">report</a>}</div>}
                <div className="mt-3"><div className="overline text-muted-foreground mb-1">Owner / Admin Privileges</div><div className="flex flex-wrap gap-1.5">{(c.owner_privileges || []).map((o) => <Tag key={o}>{o}</Tag>)}</div></div>
                <div className="mt-2"><div className="overline text-muted-foreground mb-1">Identified Risks</div><ul className="text-sm space-y-1">{(c.risks || []).map((r) => <li key={r} className="flex gap-2 text-foreground"><ShieldAlert className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />{r}</li>)}</ul></div>
              </SectionCard>
            )) : <EmptyState title="No smart contracts detected yet" sub="Contracts are captured automatically once deployed and verifiable on-chain." icon={ShieldAlert} />}
            <Disclaimer>Automated contract analysis is not a formal security audit.</Disclaimer>
          </TabsContent>

          <TabsContent value="Team">
            <div className="mb-3"><span className="overline text-muted-foreground">Team Visibility: </span><Tag>{p.team?.visibility}</Tag></div>
            {(p.team?.members || []).length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {p.team.members.map((m, i) => (
                  <div key={i} className="bg-card border border-border/60 p-4">
                    <div className="flex items-center gap-3">
                      {m.avatar ? <img src={m.avatar} alt="" className="w-11 h-11 rounded-sm object-cover" /> : <Monogram symbol={m.name?.[0]} name={m.name} size={44} />}
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">{m.name}</div>
                        <div className="text-xs text-muted-foreground">{m.role}</div>
                      </div>
                      {m.verified ? <CheckCircle2 className="w-4 h-4 text-emerald-600 ml-auto" /> : <Circle className="w-4 h-4 text-muted-foreground ml-auto" />}
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">{m.experience}</div>
                    {(m.prev_companies || []).length > 0 && <div className="flex flex-wrap gap-1 mt-2">{m.prev_companies.map((c) => <Tag key={c}>{c}</Tag>)}</div>}
                    <div className="flex gap-2 mt-3">
                      {m.linkedin && <a href={m.linkedin} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary"><Linkedin className="w-4 h-4" /></a>}
                      {m.twitter && <a href={m.twitter} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary"><Twitter className="w-4 h-4" /></a>}
                      {m.github && <a href={m.github} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary"><Github className="w-4 h-4" /></a>}
                    </div>
                  </div>
                ))}
              </div>
            ) : <EmptyState title="Team is anonymous or undisclosed" sub="No verifiable team members were found. Treat with caution." icon={ShieldAlert} />}
            <Disclaimer className="mt-4">Verified facts are distinguished from unverified associations. We do not make unsupported accusations.</Disclaimer>
          </TabsContent>

          <TabsContent value="Investors">
            {(p.investors || []).length ? (
              <SectionCard title={`Investors & Funding · Total ${fmtUSD((p.investors || []).reduce((a, x) => a + (x.amount || 0), 0))}`}>
                <table className="w-full text-sm">
                  <thead><tr className="overline text-muted-foreground"><th className="text-left pb-2">Investor</th><th className="text-left pb-2">Type</th><th className="text-left pb-2">Round</th><th className="text-right pb-2">Amount</th><th className="text-right pb-2">Date</th></tr></thead>
                  <tbody>{p.investors.map((iv, i) => <tr key={i} className="border-t border-border/40"><td className="py-2 font-medium">{iv.name}</td><td>{iv.type}</td><td>{iv.round}</td><td className="text-right num text-emerald-700">{fmtUSD(iv.amount)}</td><td className="text-right num text-muted-foreground">{fmtDate(iv.date)}</td></tr>)}</tbody>
                </table>
              </SectionCard>
            ) : <EmptyState title="No disclosed investors" sub="No verifiable funding rounds were found." />}
          </TabsContent>

          <TabsContent value="Legal">
            <SectionCard title="Legal & Regulatory">
              <Grid>
                {[["Legal Entity", p.legal?.entity_name], ["Jurisdiction", p.legal?.jurisdiction], ["Registration No.", p.legal?.reg_number], ["Incorporation Date", p.legal?.incorporation_date], ["Corporate Structure", p.legal?.structure], ["Regulator", p.legal?.regulator], ["License", p.legal?.license], ["Token Classification", p.legal?.token_classification], ["KYC Policy", p.legal?.kyc_policy ? "Yes" : "No"], ["AML Policy", p.legal?.aml_policy ? "Yes" : "No"]].map(([k, v]) => <KV key={k} label={k}>{v}</KV>)}
              </Grid>
              <div className="mt-2"><div className="overline text-muted-foreground mb-1">Restricted Countries</div><div className="flex flex-wrap gap-1.5">{(p.legal?.restricted_countries || []).map((c) => <Tag key={c}>{c}</Tag>)}</div></div>
              {(p.legal?.flags || []).length > 0 && <div className="mt-3"><div className="overline text-muted-foreground mb-1">Regulatory Considerations</div><ul className="text-sm space-y-1">{p.legal.flags.map((fl) => <li key={fl} className="flex gap-2"><ShieldAlert className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />{fl}</li>)}</ul></div>}
              <Disclaimer className="mt-4">Regulatory information is for research purposes and does not constitute legal advice.</Disclaimer>
            </SectionCard>
          </TabsContent>

          <TabsContent value="Community">
            <SectionCard title={`Social & Community · Sentiment: ${p.social?.sentiment || "—"}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {(p.social?.channels || []).map((ch) => (
                  <a key={ch.platform} href={ch.url} target="_blank" rel="noreferrer" className="border border-border/60 p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between"><span className="font-semibold text-sm">{ch.platform}</span>{ch.verified && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}</div>
                    <div className="num font-heading font-extrabold text-2xl mt-1">{fmtNum(ch.followers)}</div>
                    <div className="text-xs text-muted-foreground mt-1">Engagement: {ch.engagement} · Growth: {ch.growth}</div>
                  </a>
                ))}
              </div>
              {p.social?.notes && <p className="text-sm text-muted-foreground mt-3">{p.social.notes}</p>}
            </SectionCard>
          </TabsContent>

          <TabsContent value="Marketing">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {[["Pre-Launch", p.marketing?.prelaunch], ["Launch", p.marketing?.launch], ["Post-Launch", p.marketing?.postlaunch]].map(([k, arr]) => (
                <SectionCard key={k} title={k}>
                  <ul className="text-sm space-y-1.5">{(arr || []).length ? arr.map((x) => <li key={x} className="flex gap-2"><span className="text-primary">▸</span>{x}</li>) : <li className="text-muted-foreground">Not Found</li>}</ul>
                </SectionCard>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="Exchange / Liquidity">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SectionCard title="Exchange Strategy">
                <KV label="Confirmed Exchanges">{(p.exchanges?.confirmed || []).join(", ") || "Not Found"}</KV>
                <KV label="Planned / Rumored">{(p.exchanges?.planned || []).join(", ") || "Not Found"}</KV>
                <KV label="Initial Liquidity">{p.exchanges?.liquidity}</KV>
              </SectionCard>
              <SectionCard title="Post-Launch Listings">
                {(p.exchanges?.listings || []).length ? p.exchanges.listings.map((x, i) => <div key={i} className="text-sm">{x}</div>) : <span className="text-sm text-muted-foreground">No live listings tracked yet.</span>}
              </SectionCard>
            </div>
          </TabsContent>

          <TabsContent value="Roadmap">
            <SectionCard title="Roadmap & Execution">
              {(p.roadmap || []).map((r, i) => {
                const Icon = STATUS_ICON[r.status] || Circle;
                const color = r.status === "Completed" ? "text-emerald-600" : r.status === "In progress" ? "text-blue-600" : "text-muted-foreground";
                return (
                  <div key={i} className="flex items-center gap-3 py-3 border-b border-border/40 last:border-0">
                    <Icon className={cn("w-4 h-4 shrink-0", color)} />
                    <span className="num text-xs text-muted-foreground w-20">{r.quarter}</span>
                    <span className="text-sm flex-1">{r.milestone}</span>
                    <Tag>{r.status}</Tag>
                  </div>
                );
              })}
            </SectionCard>
          </TabsContent>

          <TabsContent value="Post-Launch">
            {p.post_launch && Object.keys(p.post_launch).length ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[["Price", `$${p.post_launch.price}`], ["Listing Price", `$${p.post_launch.listing}`], ["ATH", `$${p.post_launch.ath}`], ["ATL", `$${p.post_launch.atl}`], ["24h Volume", fmtUSD(p.post_launch.volume)], ["Market Cap", fmtUSD(p.post_launch.mcap)], ["FDV", fmtUSD(p.post_launch.fdv)], ["Holders", fmtNum(p.post_launch.holders)]].map(([k, v]) => (
                  <div key={k} className="bg-card border border-border/60 p-4"><div className="overline text-muted-foreground">{k}</div><div className="num font-heading font-bold text-xl mt-1">{v}</div></div>
                ))}
                <div className="bg-card border border-border/60 p-4 col-span-2"><div className="overline text-muted-foreground">Roadmap Delivery Score</div><div className="flex items-center gap-2 mt-2"><span className="num font-heading font-extrabold text-2xl">{p.post_launch.delivery}</span><div className="flex-1"><ScoreBar score={p.post_launch.delivery} showValue={false} /></div></div></div>
              </div>
            ) : <EmptyState title="Not launched yet" sub="Post-launch metrics (price, liquidity, holders, roadmap delivery) begin tracking after TGE." icon={Clock} />}
          </TabsContent>

          <TabsContent value="Risk / Red Flags">
            {(p.red_flags || []).length ? (
              <div className="space-y-3">
                {p.red_flags.map((rf, i) => (
                  <div key={i} className="bg-card border border-border/60 p-4" data-testid={`redflag-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="font-semibold text-sm">{rf.title}</div>
                      <RiskBadge level={rf.severity} />
                    </div>
                    <p className="text-sm text-muted-foreground mt-1.5">{rf.description}</p>
                    <div className="mt-2 text-xs text-muted-foreground"><span className="font-semibold text-foreground">Evidence: </span>{rf.evidence}</div>
                    {rf.source && <a href={rf.source} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1 mt-1 hover:underline">Source <ExternalLink className="w-3 h-3" /></a>}
                  </div>
                ))}
              </div>
            ) : <EmptyState title="No red flags detected" sub="The red-flag engine found no material concerns based on current data." icon={CheckCircle2} />}
          </TabsContent>

          <TabsContent value="Sources">
            <SectionCard title="Sources" className="mb-4">
              {(p.sources || []).map((s, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-border/40 last:border-0">
                  <VerificationBadge status={s.verification_status} />
                  <a href={s.url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline flex-1 truncate">{s.url}</a>
                  <Tag>{s.type}</Tag>
                  <span className="text-xs text-muted-foreground">Reliability: {s.reliability}</span>
                </div>
              ))}
            </SectionCard>
            <SectionCard title="Structured Facts & Provenance">
              <table className="w-full text-sm">
                <thead><tr className="overline text-muted-foreground"><th className="text-left pb-2">Field</th><th className="text-left pb-2">Value</th><th className="text-left pb-2">Source Type</th><th className="text-left pb-2">Verification</th><th className="text-right pb-2">Confidence</th></tr></thead>
                <tbody>{(p.facts || []).map((fct, i) => <tr key={i} className="border-t border-border/40"><td className="py-2 font-medium">{fct.field}</td><td className="num">{fct.value}</td><td className="text-muted-foreground text-xs">{fct.source_type}</td><td><VerificationBadge status={fct.verification_status} /></td><td className="text-right text-xs">{fct.confidence}</td></tr>)}</tbody>
              </table>
            </SectionCard>
          </TabsContent>

          <TabsContent value="AI Analysis">
            <SectionCard title={`ICO Intelligence Score — ${sc.overall}/100`} className="mb-4">
              <p className="text-sm text-muted-foreground mb-4">Transparent, component-weighted score. Each component shows its reasoning, evidence, and confidence.</p>
              <div className="space-y-2.5">
                {(sc.components || []).map((c) => (
                  <div key={c.name} className="border border-border/60 p-3" data-testid={`score-comp-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <span className="text-sm font-semibold">{c.name}</span>
                      <div className="flex items-center gap-3 w-48"><ScoreBar score={c.score} /><ConfidenceBadge level={c.confidence} /></div>
                    </div>
                    <div className="text-xs text-muted-foreground"><span className="font-medium text-foreground">Reason:</span> {c.reason}</div>
                    <div className="text-xs text-muted-foreground"><span className="font-medium text-foreground">Evidence:</span> {c.evidence}</div>
                  </div>
                ))}
              </div>
              <Disclaimer className="mt-4">Scores are analytical research signals, not investment advice.</Disclaimer>
            </SectionCard>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
